import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormLabel,
  List,
  ListItem,
  ListItemText,
} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import { isBlank, mapIndexed, notBlank } from "../../util/ext";
import { append, lensPath, lensProp, pick, remove, set } from "ramda";
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/styles";
import Button from "@material-ui/core/Button";
import { useErrorBox } from "../../manage-organizations/edit/use-errorBox";
import {
  validateLandingPage,
  validateLandingPageField,
  validateSocialLink,
} from "../cspProjectValidation";
import RichTextField from "../../creator-application/common/RichTextField";
import FormControl from "@material-ui/core/FormControl";
import Typography from "@material-ui/core/Typography";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import SocialLinkPlatformSelect from "./SocialLinkPlatformSelect";
import { getSocialLinkPlatform } from "../cspServices";
import ConfirmButton from "../../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  title: {
    fontSize: "1.8rem",
  },

  rte: {
    borderWidth: 2,
    marginTop: theme.spacing(1),
    fontFamily: "inherit",
    padding: 10,
    overflowY: "auto",
    resize: "vertical",
    minHeight: 250,

    "& a": {
      color: theme.palette.primary.main,
    },
  },
}));

const EditLandingPageDialog = ({ open, project, onCancel, onApply }) => {
  const classes = useStyles();

  const [fields, setFields] = useState({
    landingPageTemplate: "landing-page",
    thankYouPageTemplate: "thank-you",
    collectionUri: null,
    projectDescription: null,
    additionalInformation: null,
    ageRequirements: null,
    thankYouMessage: null,
    socialLinks: [],
  });

  useEffect(() => {
    if (open && notBlank(project)) {
      setFields(
        pick(
          [
            "landingPageTemplate",
            "thankYouPageTemplate",
            "collectionUri",
            "projectDescription",
            "additionalInformation",
            "ageRequirements",
            "thankYouMessage",
            "socialLinks",
          ],
          project,
        ),
      );
    }
  }, [open, project]);

  const {
    addValidationError,
    removeValidationError,
    removeValidationErrors,
    setValidationErrors,
    hasValidationError,
    firstValidationError,
    ErrorBox: DialogErrors,
  } = useErrorBox();

  const handleBlur = (field, value) => {
    const errors = validateLandingPageField(field, value);
    if (notBlank(errors)) {
      addValidationError(errors);
    } else {
      removeValidationError(field);
    }
  };

  const handleTextChange = path => event => {
    const value = event.target.value;
    setFields(prev => set(lensPath(path), value, prev));
  };

  const handleRteChange = path => ({ html, text }) => {
    if (notBlank(text)) {
      setFields(prev => set(lensPath(path), html, prev));
    } else {
      setFields(prev => set(lensPath(path), null, prev));
    }
  };

  const [socialLinkPlatform, setSocialLinkPlatform] = useState(null);
  const [socialLinkUrl, setSocialLinkUrl] = useState(null);

  const handleAddSocialLinkClick = useCallback(() => {
    const errors = validateSocialLink({ socialLinkPlatform, socialLinkUrl });
    if (notBlank(errors)) {
      setValidationErrors(errors);
    } else {
      setFields(prev =>
        set(
          lensProp("socialLinks"),
          append(
            { platform: socialLinkPlatform, linkUrl: socialLinkUrl },
            prev.socialLinks,
          ),
          prev,
        ),
      );
      setSocialLinkPlatform("");
      setSocialLinkUrl("");
      removeValidationErrors();
    }
  }, [
    socialLinkPlatform,
    socialLinkUrl,
    setValidationErrors,
    removeValidationErrors,
  ]);

  const handleDeleteSocialLink = idx => {
    setFields(prev =>
      set(lensProp("socialLinks"), remove(idx, 1, prev.socialLinks), prev),
    );
  };
  const handleApply = useCallback(async () => {
    const validationErrors = validateLandingPage(fields);
    if (notBlank(validationErrors)) {
      setValidationErrors(validationErrors);
    } else {
      onApply(fields);
    }
  }, [fields, onApply, setValidationErrors]);

  return (
    <Dialog open={open} maxWidth="lg" fullWidth>
      <DialogTitle id="edit-landing-page-dialog-title">
        Edit Project Landing Page Properties
      </DialogTitle>
      <DialogContent>
        <DialogErrors />
        <Grid container spacing={6} alignItems="flex-end">
          <Grid item sm={6}>
            <TextField
              autoFocus
              id="landing-page-template"
              fullWidth
              label="Landing Page Template"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.landingPageTemplate}
              onChange={handleTextChange(["landingPageTemplate"])}
              error={hasValidationError("landingPageTemplate")}
              helperText={firstValidationError("landingPageTemplate")}
              onBlur={() =>
                handleBlur("landingPageTemplate", fields.landingPageTemplate)
              }
            />
          </Grid>
          <Grid item sm={6}>
            <TextField
              id="thank-you-template"
              fullWidth
              label="Thank You Page Template"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.thankYouPageTemplate}
              onChange={handleTextChange(["thankYouPageTemplate"])}
              error={hasValidationError("thankYouPageTemplate")}
              helperText={firstValidationError("thankYouPageTemplate")}
              onBlur={() =>
                handleBlur("thankYouPageTemplate", fields.thankYouPageTemplate)
              }
            />
          </Grid>
          <Grid item sm={6}>
            <TextField
              id="collection-uri"
              fullWidth
              label="BiblioBoard Collection URL"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.collectionUri}
              onChange={handleTextChange(["collectionUri"])}
              error={hasValidationError("collectionUri")}
              helperText={firstValidationError("collectionUri")}
              onBlur={() => handleBlur("collectionUri", fields.collectionUri)}
            />
          </Grid>
          <Grid item sm={6}>
            <TextField
              id="age-requirements"
              fullWidth
              label="Age Requirements (optional)"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.ageRequirements}
              onChange={handleTextChange(["ageRequirements"])}
              error={hasValidationError("ageRequirements")}
              helperText={firstValidationError("ageRequirements")}
              onBlur={() =>
                handleBlur("ageRequirements", fields.ageRequirements)
              }
            />
          </Grid>

          <Grid item sm={12}>
            <FormControl id="project-description" fullWidth>
              <FormLabel component="legend">About this Project</FormLabel>
              <RichTextField
                enableLinks
                label="About this Project"
                value={fields.projectDescription || ""}
                className={classes.rte}
                onChange={handleRteChange(["projectDescription"])}
                error={hasValidationError("projectDescription")}
                helperText={firstValidationError("projectDescription")}
                onBlur={() =>
                  handleBlur("projectDescription", fields.projectDescription)
                }
              />
            </FormControl>
          </Grid>
          <Grid item sm={12}>
            <FormControl id="additional-information" fullWidth>
              <FormLabel component="legend">Additional Information</FormLabel>
              <RichTextField
                enableLinks
                label="Additional Information"
                value={fields.additionalInformation || ""}
                className={classes.rte}
                onChange={handleRteChange(["additionalInformation"])}
                error={hasValidationError("additionalInformation")}
                helperText={firstValidationError("additionalInformation")}
                onBlur={() =>
                  handleBlur(
                    "additionalInformation",
                    fields.additionalInformation,
                  )
                }
              />
            </FormControl>
          </Grid>
          <Grid item sm={12}>
            <FormControl id="thank-you-message" fullWidth>
              <FormLabel component="legend">Thank You Page Content</FormLabel>
              <RichTextField
                enableLinks
                label="Thank You Page Content"
                value={fields.thankYouMessage || ""}
                className={classes.rte}
                onChange={handleRteChange(["thankYouMessage"])}
                error={hasValidationError("thankYouMessage")}
                helperText={firstValidationError("thankYouMessage")}
                onBlur={() =>
                  handleBlur("thankYouMessage", fields.thankYouMessage)
                }
              />
            </FormControl>
          </Grid>

          <Grid item md={12}>
            <Typography variant="h5">Social Links</Typography>
            {notBlank(fields.socialLinks) ? (
              <List dense>
                {mapIndexed(
                  (sl, idx) => (
                    <ListItem key={sl.projectSocialLinkId}>
                      <ListItemText
                        primary={getSocialLinkPlatform(sl.platform).label}
                        secondary={sl.linkUrl}
                      />
                      <ListItemSecondaryAction>
                        <ConfirmButton
                          handleDelete={() => handleDeleteSocialLink(idx)}
                          dialogTitle="Remove Social Link"
                          dialogBody="Are you sure you want to remove this social link?"
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ),
                  fields.socialLinks,
                )}
              </List>
            ) : (
              <Box padding={5} fontSize="1.4rem" fontStyle="italic">
                No Social Links are set up for this Project.
              </Box>
            )}
          </Grid>

          <Grid item md={5}>
            <SocialLinkPlatformSelect
              id="social-link-platform"
              title="New Social Link Platform"
              value={socialLinkPlatform}
              onChange={event => setSocialLinkPlatform(event.target.value)}
              error={hasValidationError("socialLinkPlatform")}
              helperText={firstValidationError("socialLinkPlatform")}
            />
          </Grid>
          <Grid item md={5}>
            <TextField
              id="social-link-url"
              fullWidth
              label="New Social Link URL"
              InputProps={{
                style: { fontSize: "1.8rem" },
              }}
              value={socialLinkUrl}
              onChange={event => setSocialLinkUrl(event.target.value)}
              error={hasValidationError("socialLinkUrl")}
              helperText={firstValidationError("socialLinkUrl")}
            />
          </Grid>
          <Grid item md={2}>
            <Button
              variant="outlined"
              onClick={handleAddSocialLinkClick}
              disabled={
                isBlank(socialLinkPlatform) || isBlank(setSocialLinkUrl)
              }
              aria-label="add option"
            >
              Add Social Link
            </Button>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditLandingPageDialog;
